import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.carauction.io';

class AuctionService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  // Create vehicle listing
  async createVehicle(vehicleData) {
    try {
      const response = await this.api.post('/vehicles', vehicleData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create vehicle',
      };
    }
  }

  // Parse VIN
  async parseVIN(vin) {
    try {
      const response = await this.api.post('/vehicles/parse-vin', { vin });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to parse VIN',
      };
    }
  }

  // Upload photo
  async uploadPhoto(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await this.api.post('/vehicles/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to upload photo',
      };
    }
  }

  // Create auction
  async createAuction(auctionData) {
    try {
      const response = await this.api.post('/auctions', auctionData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create auction',
      };
    }
  }

  // Get auctions for seller
  async getSellerAuctions(status = 'all') {
    try {
      const response = await this.api.get(`/auctions?status=${status}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch auctions',
      };
    }
  }

  // Get single auction
  async getAuction(auctionId) {
    try {
      const response = await this.api.get(`/auctions/${auctionId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch auction',
      };
    }
  }

  // Pause auction
  async pauseAuction(auctionId) {
    try {
      const response = await this.api.patch(`/auctions/${auctionId}/pause`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to pause auction',
      };
    }
  }

  // Resume auction
  async resumeAuction(auctionId) {
    try {
      const response = await this.api.patch(`/auctions/${auctionId}/resume`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to resume auction',
      };
    }
  }

  // Extend auction
  async extendAuction(auctionId, hours) {
    try {
      const response = await this.api.patch(`/auctions/${auctionId}/extend`, {
        hours,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to extend auction',
      };
    }
  }

  // Get auction bids
  async getAuctionBids(auctionId) {
    try {
      const response = await this.api.get(`/auctions/${auctionId}/bids`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch bids',
      };
    }
  }

  // Get payouts
  async getPayouts() {
    try {
      const response = await this.api.get('/payouts');
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch payouts',
      };
    }
  }

  // Get seller analytics
  async getAnalytics() {
    try {
      const response = await this.api.get('/seller/analytics');
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch analytics',
      };
    }
  }

  // Send message
  async sendMessage(recipientId, message) {
    try {
      const response = await this.api.post('/messages', {
        recipientId,
        message,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to send message',
      };
    }
  }

  // Get messages
  async getMessages(conversationId) {
    try {
      const response = await this.api.get(`/messages/${conversationId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch messages',
      };
    }
  }

  // Get seller performance
  async getPerformance() {
    try {
      const response = await this.api.get('/seller/performance');
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch performance',
      };
    }
  }
}

export default new AuctionService();
